import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import HeroSlideshow from '../components/HeroSlideshow';
import { stats, programs, steps, companies, heroSlides } from '../mock';

export default function Home() {
  const ref = useReveal();

  return (
    <div ref={ref} className="max-w-6xl mx-auto px-5 sm:px-8">
      {/* HERO */}
      <section className="pt-10 sm:pt-16 pb-6">
        <div className="bg-white rounded-[28px] overflow-hidden shadow-[0_30px_80px_-40px_rgba(14,23,38,0.35)] grid md:grid-cols-2 reveal in">
          <div className="p-8 sm:p-12 lg:p-14 flex flex-col justify-center">
            <span className="eyebrow text-[#2E6BFF]">A practical school for ambitious product people</span>
            <h1 className="font-display font-extrabold text-[#0E1726] leading-[0.95] tracking-tight text-[46px] sm:text-[58px] lg:text-[64px] mt-5">
              Build products.<br />
              Build <span className="text-[#2E6BFF]">influence.</span><br />
              Build your next chapter.
            </h1>
            <p className="text-[#5B6472] text-[17px] leading-relaxed mt-6 max-w-md">
              Learn from two decades of building and scaling technology products—not theory alone.
              Get the judgment, confidence and practical tools to lead at the next level.
            </p>
            <div className="flex flex-wrap items-center gap-5 mt-8">
              <Link
                to="/programs"
                data-cta="Explore programs"
                className="group bg-[#2E6BFF] text-white font-semibold px-6 py-3.5 rounded-full flex items-center gap-2 hover:bg-[#1f5aef] transition-colors"
              >
                Explore programs
                <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/contact" data-cta="Talk to Rajni" className="font-semibold text-[#0E1726] flex items-center gap-1.5 hover:text-[#2E6BFF] transition-colors">
                Talk to Rajni
              </Link>
            </div>
          </div>
          <div className="relative min-h-[320px] md:min-h-full">
            <HeroSlideshow images={heroSlides} interval={3000} />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-14 reveal">
        <p className="font-display font-bold text-[#0E1726] text-[22px] sm:text-[26px] max-w-xl">
          From doing the work to leading the work
        </p>
        <p className="text-[#5B6472] mt-1">Career clarity, product craft, AI fluency and executive judgment.</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-9">
          {stats.map((s) => (
            <div key={s.label} className="border-l-2 border-[#2E6BFF]/40 pl-4">
              <div className="font-display font-extrabold text-[#0E1726] text-[26px] leading-tight">{s.value}</div>
              <div className="text-[#5B6472] text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAMS */}
      <section id="learn" className="py-10">
        <div className="reveal max-w-2xl">
          <h2 className="font-display font-extrabold text-[#0E1726] text-[32px] sm:text-[40px] tracking-tight">
            Choose the support your next chapter needs.
          </h2>
          <p className="text-[#5B6472] text-[17px] mt-4 leading-relaxed">
            Focused experiences for individuals, product teams, and organizations—from career growth
            and AI capability to strategy and leadership transformation.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {programs.map((p) => (
            <div
              key={p.index}
              className="reveal group bg-white rounded-2xl p-7 sm:p-8 shadow-[0_20px_50px_-35px_rgba(14,23,38,0.4)] border-t-[3px] hover:-translate-y-1 transition-transform duration-300"
              style={{ borderTopColor: p.accent }}
            >
              <div className="eyebrow" style={{ color: p.accent }}>{p.index} · {p.kicker}</div>
              <h3 className="font-display font-bold text-[#0E1726] text-[24px] mt-3">{p.title}</h3>
              <p className="text-[#5B6472] leading-relaxed mt-3">{p.desc}</p>
              <ul className="mt-5 space-y-3">
                {p.points.map((pt) => (
                  <li key={pt} className="flex items-center gap-3 text-[#0E1726] border-b border-black/5 pb-3">
                    <Check size={16} style={{ color: p.accent }} />
                    <span className="text-[15px]">{pt}</span>
                  </li>
                ))}
              </ul>
              <Link to={p.kicker === 'LEARN' ? '/enroll' : '/contact'} className="inline-flex items-center gap-1.5 mt-6 font-semibold text-[15px] hover:gap-2.5 transition-all" style={{ color: p.accent }}>
                {p.cta} <ArrowRight size={16} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16">
        <div className="reveal max-w-2xl">
          <h2 className="font-display font-extrabold text-[#0E1726] text-[32px] sm:text-[40px] tracking-tight">
            Leave with movement, not more information.
          </h2>
          <p className="text-[#5B6472] text-[17px] mt-4">
            Every experience is built around a real decision or outcome—not passive content consumption.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {steps.map((s) => (
            <div key={s.n} className="reveal">
              <div className="font-display font-extrabold text-[#2E6BFF] text-[44px] leading-none">{s.n}</div>
              <h3 className="font-display font-bold text-[#0E1726] text-[20px] mt-4">{s.title}</h3>
              <p className="text-[#5B6472] leading-relaxed mt-2">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-10 grid md:grid-cols-[0.85fr_1.15fr] gap-8 items-start">
        <div className="reveal bg-[#2E6BFF] text-white rounded-2xl p-8 sm:p-10">
          <div className="font-display font-extrabold text-[52px] leading-[0.9]">20<br />years.</div>
          <p className="text-white/85 mt-5 leading-relaxed">
            One career across startups and global technology companies—turned into practical lessons for yours.
          </p>
        </div>
        <div className="reveal">
          <span className="eyebrow text-[#5B6472]">Meet your guide</span>
          <h2 className="font-display font-extrabold text-[#0E1726] text-[30px] sm:text-[38px] tracking-tight mt-3">
            Rajni Sharma has built products—and the organizations behind them.
          </h2>
          <p className="text-[#5B6472] leading-relaxed mt-5">
            Rajni is a product executive, founder, educator, and Forbes Technology Council member. Across
            Amazon, Apple, Microsoft, eBay, Rubrik, Paylocity, and Walmart, she has led platforms, marketplaces,
            identity systems, and enterprise products used at significant scale.
          </p>
          <p className="text-[#5B6472] leading-relaxed mt-4">
            Her teaching connects product craft with the harder skills that change careers: judgment, influence,
            storytelling, resilience, and knowing what deserves your energy.
          </p>
          <div className="flex flex-wrap gap-2.5 mt-6">
            {companies.map((c) => (
              <span key={c} className="bg-white border border-black/10 rounded-full px-4 py-2 text-sm font-semibold text-[#0E1726]">{c}</span>
            ))}
          </div>
          <Link to="/about" className="inline-flex items-center gap-1.5 mt-7 font-semibold text-[#2E6BFF] hover:gap-2.5 transition-all">
            Read Rajni's full story <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10">
        <div className="reveal bg-[#F6B93B] rounded-[28px] p-9 sm:p-14 grid md:grid-cols-[1.4fr_0.6fr] gap-6 items-center">
          <div>
            <h2 className="font-display font-extrabold text-[#0E1726] text-[34px] sm:text-[46px] tracking-tight leading-[1.02]">
              You don't need another generic framework.
            </h2>
            <p className="text-[#0E1726]/80 mt-4 max-w-lg leading-relaxed">
              You need the right next move. Tell me what you are trying to change, and we'll find the best place to begin.
            </p>
          </div>
          <div className="md:justify-self-end">
            <Link to="/contact" data-cta="Talk to Rajni" className="group bg-[#0E1B2E] text-white font-semibold px-6 py-3.5 rounded-full inline-flex items-center gap-2 hover:bg-[#1b2a44] transition-colors">
              Start a conversation
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
